import { AuthResponse } from "../types/Auth";
import { AnexoChamado } from "../types/Chamados";
import { ClientAddress } from "../types/ClientAddress";
import { ContratoCotacao } from "../types/Contrato";
import { CotacaoEcommerce } from "../types/Cotacao";
import { CurrentContract } from "../types/CurrentContract";
import { Pending } from "../types/Pending";
import { PersonalInfoBackend } from "../types/PersonalData";
import { OpenQuoteApiResponse } from "../types/OpenQuote";
import { RegistrationData } from "../types/RegistrationData";
import { Veiculo as VeiculoTipo } from "../types/Veiculo";
import api from "./api";
import apiHmlg from "./apiHmlg";

const API_TARGET = import.meta.env.VITE_API_TARGET || "prod";
const ApiAmbient = API_TARGET.toLowerCase() === "hmlg" ? apiHmlg : api;


export const login = async (
  email: string,
  senha: string,
): Promise<AuthResponse> => {
  const { data } = await ApiAmbient.post<AuthResponse>(
    "/cliente/api_autenticar_cliente.php",
    { login: email, senha },
  );
  return data;
};

export const loginWithToken = async (
  token: string,
  cve_codigo: string,
): Promise<AuthResponse> => {
  const { data } = await ApiAmbient.post<AuthResponse>(
    "/cliente/api_autenticar_cliente.php",
    { token, cve_codigo },
  );
  return data;
};

export const getRegistrationData = async (
  cli_codigo: string,
): Promise<RegistrationData> => {
  const { data } = await ApiAmbient.post<RegistrationData>(
    "/cliente/api_dados_cadastrais.php",
    { cli_codigo },
  );
  return data;
};

export const saveAddress = async (
  cli_codigo: string,
  address: ClientAddress,
) => {
  const addressData: Partial<ClientAddress> & { cli_codigo: string } = {
    ...address,
    cli_codigo,
  };

  if (addressData.cle_numero) {
    addressData.cle_numero = addressData.cle_numero.replace(/[^0-9]/g, "");
  }

  if (!address.cle_codigo || address.cle_codigo === "") {
    delete addressData.cle_codigo;
  }

  await ApiAmbient.post("/cliente/api_atualizar_dados_cadastrais.php", {
    enderecos: [addressData],
  });
};

export const saveVehicle = async (cli_codigo: string, vehicle: VeiculoTipo) => {
  const vehicleData: VeiculoTipo & { cli_codigo: string } = {
    ...vehicle,
    cli_codigo,
  };

  if (vehicle.clv_codigo) {
    vehicleData.clv_codigo = vehicle.clv_codigo;
  } else {
    if (vehicle.vmd_codigo) {
      vehicleData.vmd_codigo = vehicle.vmd_codigo;
    }
    if (vehicle.cor_codigo) {
      vehicleData.cor_codigo = vehicle.cor_codigo;
    }
  }

  await ApiAmbient.post("/cliente/api_atualizar_dados_cadastrais.php", {
    veiculos: [vehicleData],
  });
};

export const savePersonalInfo = async (
  cli_codigo: string,
  data: Partial<PersonalInfoBackend>,
) => {
  await ApiAmbient.post("/cliente/api_atualizar_dados_cadastrais.php", {
    pessoais: { ...data, cli_codigo },
  });
};

export const uploadAttachment = async (
  sigla: string,
  registro: string,
  file: File,
) => {
  const form = new FormData();
  form.append("sigla", sigla);
  form.append("registro", registro);
  form.append("file", file);
  await ApiAmbient.post("/cliente/api_upload_anexos.php", form, {
    headers: { "Content-Type": "multipart/form-data" },
    transformRequest: (f) => f,
  });
};

export const getAttachments = async (
  sigla: string,
  registro: string,
): Promise<AnexoChamado[]> => {
  const { data } = await ApiAmbient.post<AnexoChamado[]>(
    "/cliente/api_carregar_anexos.php",
    {
      sigla,
      registro,
    },
  );
  return data;
};

export const getCurrentContract = async (
  cli_codigo: string,
): Promise<CurrentContract[]> => {
  const { data } = await ApiAmbient.post<CurrentContract[]>(
    "/cliente/api_contrato_corrente.php",
    { cli_codigo },
  );
  return data;
};

export const getHistorico = async (
  cli_codigo: string,
): Promise<ContratoCotacao[]> => {
  const { data } = await ApiAmbient.post<ContratoCotacao[]>(
    "/cliente/api_historico_transportes.php",
    { cli_codigo },
  );
  return data;
};

export const getCotacaoDetalhes = async (
  cve_codigo: string,
): Promise<CotacaoEcommerce[]> => {
  const { data } = await ApiAmbient.get<CotacaoEcommerce[]>(
    "/cliente/api_cotacao_ecommerce_json.php",
    { params: { cve_codigo } },
  );
  return data;
};

export const getPendencias = async (cli_codigo: string): Promise<Pending[]> => {
  const { data } = await ApiAmbient.post<Pending[]>(
    "/cliente/api_pendencias.php",
    {
      cli_codigo,
    },
  );
  return data;
};

export const getCotacoesEmAberto = async (
  cli_codigo: string,
): Promise<OpenQuoteApiResponse[]> => {
  const { data } = await ApiAmbient.post<OpenQuoteApiResponse[]>(
    "/cliente/api_cotacoes_em_aberto.php",
    { cli_codigo },
  );
  return data;
};

export const getTrackingSteps = async (
  cve_codigo: string,
  cve_chassi: string,
): Promise<string[]> => {
  const { data } = await ApiAmbient.post<string[]>(
    "/cliente/api_rastreamento_cliente.php",
    { cve_codigo, cve_chassi },
  );
  return data;
};

export const recoverPassword = async (email: string) => {
  const { data } = await ApiAmbient.post("cliente/api_recuperar_senha.php", {
    email,
  });
  return data;
};

export const authenticateRecovery = async (
  login: string,
  codigo_recuperacao: string,
) => {
  const { data } = await ApiAmbient.post(
    "/cliente/api_autenticar_recuperacao.php",
    {
      login,
      codigo_recuperacao,
    },
  );
  return data;
};

export const changePassword = async (login: string, senha_nova: string) => {
  const { data } = await ApiAmbient.post("/cliente/api_alterar_senha.php", {
    login,
    senha_nova,
  });
  return data;
};
